import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password, remember_me: rememberMe },
      { withCredentials: true }
    );
    // If the user has 2FA enabled the backend short-circuits with a
    // pending token instead of a session. The caller is responsible
    // for prompting the user for the TOTP code and calling verify2FA.
    if (response.data?.requires_2fa) {
      return { requires_2fa: true, pending_token: response.data.pending_token };
    }
    setUser(response.data);
    return response.data;
  };

  const verify2FA = async (pendingToken, code) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/verify`,
      { pending_token: pendingToken, code },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, firstName, lastName, phone, betaCouponCode = '', billing = null) => {
    const payload = { email, password, first_name: firstName, last_name: lastName, phone, origin_url: window.location.origin };
    if (betaCouponCode) payload.beta_coupon_code = betaCouponCode;
    if (billing) Object.assign(payload, billing);
    const response = await axios.post(`${API_URL}/api/auth/register`,
      payload,
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSessionId = async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/session`, 
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      verify2FA,
      register, 
      loginWithGoogle,
      exchangeSessionId,
      logout,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
